'use client'
/***
 *
 * @file components/MenuList/index.tsx
 * @description MenuList component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Centres
 * @since 13 Feb 2024
 *
 */

import React from "react";
// import "./MenuList.scss";
import MenuItem from "../MenuItem";
import classNames from "classnames";
import { useResponsive } from "@/client/hooks/useResponsive";

/**
 * type of props to pass inside MenuList functional component
 */
type T_Props = {
  /** id content to be displayed inside the Top menu */
  id?: any,

  /** data content to be displayed inside the Top menu */
  data?: any,

  /** parentIndex content to be displayed inside the Top menu */
  parentIndex?: any,

  /** CSS class to be added to the MenuList component */
  className?: string,

  /** custom CSS styles to be passed in the style html attribute inside MenuList */
  customStyles?: any,

  /** to set the alignment for menu items */
  menuAlignment?: string

  /** to set the ariaLabelledby for menu items */
  ariaLabelledby?: string

  /** action to be performed on click of the menu item */
  action?: () => void;
}

/**
 * Name: Top MenuList
 * 
 * Description: A functional component to display the Navbar throughout the application window
 * 
 * @param param0 object containing the following properties:
 *                - data: content to be displayed in the Top MenuList component
 *                - className: CSS class to be applied to the MenuList component
 *                - customStyles: CSS styles to be applied to the MenuList component 
 * @example T_Props
 * ```
 * type T_Props = {
 *      id?: any,               // optional
 *      data?: any,             // optional
 *      parentIndex?: any,      // optional
 *      className?: string      // optional
 *      customStyles?: any,     // optional
 *      menuAlignment?: string  // optional
 *      ariaLabelledby?: string // optional
 * }
 * ```
 * 
 * 
 * @returns html element navbar to be rendered on the UI
 */
const MenuList = ({data=[], parentIndex=0, id="menu-list", ariaLabelledby="", className="", action}: T_Props) => {

  /**
   * Name: MenuList
   * 
   * Description: a functional component that accepts the list of menu items from Contentful API and returns html elements to display each menu item as a respectice list item
   * 
   * @param param0 object containing the following properties:
  *                - menuList: menu items to be displayed inside the MenuList navbar component
  *                - parentIndex: index of the parent component of the MenuList
   * @returns an html list of menu items to be displayed within MenuList
   */

  const { isMd } = useResponsive();
  
  return (
    <>
      {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
      <ul id={id} className={classNames(`navbar-nav ${className}`, !isMd ? "py-2" : "")} data-testid="MenuList" aria-labelledby={ariaLabelledby}>
        {Array.isArray(data) && data?.map((menu: any, menuIndex: number) => {
            return (
              <MenuItem key={`menu-item-${menuIndex}`} data={{...menu, content: menu?.content || menu?.fields}} menuIndex={menuIndex} parentIndex={parentIndex} action={action} />
            )
            // return <li>{menu?.fields?.linkText} -- oo</li>
            // return <li><pre>{JSON.stringify(menu, null, 2)} -- oo</pre></li>
        })}
      </ul>
    </>
  );
};

export default MenuList;
